import React, { useEffect, useState, useMemo, useCallback } from "react";
import { icons } from "../../Assets/Icons/icons";
import { createPortal } from "react-dom";
import AdminCategorySkeleton from "../../components/AdminSideComponents/AdminCategory/AdminCategorySkeleton";
import CategoryDetailsModal from "../../components/AdminSideComponents/AdminCategory/CategoryDetailsModal";
import axios from "axios";
import { BaggageClaim, Search } from "lucide-react";

// Helper: extract a readable string from multilingual object { en, hi, es, zh }
const getLocalizedString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    // Prefer English, then first available language
    return value.en || value.hi || value.es || value.zh || Object.values(value)[0] || "";
  }
  return "";
};

const SupplierCategories = () => {
  const [entriesPerPage] = useState(8);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [error, setError] = useState(null);
  const [isDetailsModal, setIsDetailsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Fetch Categories - ONLY GET API
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
      );
      setCategoryData(response.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to load categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDetailsModal = (category) => {
    setSelectedCategory(category);
    setIsDetailsModal(true);
  };

  // Filter Search - use the localized name string
  const filteredCategory = useMemo(() => {
    return categoryData.filter((category) => {
      const nameStr = getLocalizedString(category.name);
      return nameStr.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [categoryData, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredCategory.length / entriesPerPage);
  const paginatedCategory = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesPerPage;
    return filteredCategory.slice(startIndex, startIndex + entriesPerPage);
  }, [filteredCategory, currentPage, entriesPerPage]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <>
      <section className="min-h-screen p-6 bg-[#EEF3F7]">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl md:text-3xl font-philosopher flex items-center gap-2">
            <BaggageClaim className="w-7 h-7 text-blue-600" />
            All Categories
          </h1>
        </div>

        {/* Search bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="relative">
              <input
                value={searchQuery}
                onChange={handleSearch}
                className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
                type="text"
                placeholder="Search Category..."
              />
              <button className="text-xl hover:text-blue-600 transition-colors duration-500">
                <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Category List */}
        <div className="my-5 min-h-[200px]">
          {loading ? (
            <AdminCategorySkeleton />
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : filteredCategory.length > 0 ? (
            <div className="grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-6">
              {paginatedCategory.map((category, index) => (
                <div
                  key={category._id || index}
                  className="relative bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 group"
                >
                  {/* Image */}
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={category.imgUrl}
                      alt={getLocalizedString(category.name)}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />

                    {/* Only View button for Supplier */}
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                      <button
                        onClick={() => handleDetailsModal(category)}
                        className="p-2 bg-white/80 rounded-full text-[#ea8d2a] hover:bg-white hover:scale-110 transition-transform"
                        title="View Details"
                      >
                        {icons.eyeIcon}
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-800 truncate capitalize">
                      {getLocalizedString(category.name)}
                    </h3>
                    <p className="text-gray-500 text-sm mt-2 line-clamp-2">
                     {getLocalizedString(category.description) || "No description available."}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-6xl text-gray-300 mb-4">
                {icons.folderIcon}
              </div>
              <h2 className="text-2xl font-semibold text-gray-500 mb-2">
                No Categories Found
              </h2>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredCategory.length > 0 && !loading && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-3">
            <h6 className="text-sm text-gray-500">
              Showing {paginatedCategory.length} of {filteredCategory.length} entries
            </h6>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
              >
                {icons.leftAngleIcon}
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i + 1)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium 
                      ${currentPage === i + 1
                        ? "bg-blue-500 text-white shadow-md"
                        : "border border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
              >
                {icons.rightAngleIcon}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* View Details Modal */}
      {isDetailsModal &&
        createPortal(
          <CategoryDetailsModal
            isOpen={isDetailsModal}
            onClose={() => setIsDetailsModal(false)}
            category={selectedCategory}
          />,
          document.body
        )}
    </>
  );
};

export default SupplierCategories;